const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const API_PREFIX = '/api/v1';

// Token storage
const TOKEN_KEY = 'nudge_auth_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Core fetch wrapper
async function apiFetch<T>(path: string, options: RequestInit = {}, _retried = false): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(`${API_BASE}${API_PREFIX}${path}`, {
    ...options,
    headers,
  });

  // Auto-store refreshed token
  const refreshed = res.headers.get('X-Refreshed-Token');
  if (refreshed) {
    setToken(refreshed);
  }

  // Handle 401 — if we had a token, session expired; otherwise let error fall through
  // (e.g., wrong password on login should show the backend's error, not "Session expired")
  if (res.status === 401 && token) {
    clearToken();
    localStorage.removeItem('nudge_state_v2');
    window.dispatchEvent(new CustomEvent('nudge:unauthorized'));
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
  }

  // Handle 429 — rate limited
  if (res.status === 429) {
    throw new ApiError(
      429,
      'RATE_LIMITED',
      'Too many attempts. Please wait a moment and try again.',
    );
  }

  // Handle error responses
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = body.error || {};
    throw new ApiError(
      res.status,
      error.code || 'UNKNOWN_ERROR',
      error.message || `Request failed with status ${res.status}`,
      error.details,
    );
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

// Convenience methods
export const api = {
  get<T>(path: string): Promise<T> {
    return apiFetch<T>(path, { method: 'GET' });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  upload<T>(path: string, formData: FormData): Promise<T> {
    return apiFetch<T>(path, {
      method: 'POST',
      body: formData,
    });
  },
};

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface AuthResponse {
  user: {
    id: number;
    name: string;
    email: string;
    timezone: string;
    roles: string[];
    weeklyGoalTarget: number;
    referralCode: string;
    photoUrl?: string;
    birthday?: string;
    notificationSettings?: {
      dailyReminders: boolean;
      birthdayReminders: boolean;
      weeklySummary: boolean;
      overdueAlerts: boolean;
      quietHours: { enabled: boolean; start: string; end: string };
    };
    createdAt: string;
  };
  token: string;
}

export const authApi = {
  checkEmail(email: string) {
    return api.post<{ exists: boolean; name?: string }>('/auth/check-email', { email });
  },

  register(data: {
    name: string;
    email: string;
    password: string;
    timezone?: string;
    referral_code?: string;
  }) {
    return api.post<AuthResponse>('/auth/register', data);
  },

  login(data: { email: string; password: string }) {
    return api.post<AuthResponse>('/auth/login', data);
  },

  google(data: { id_token: string; name?: string; timezone?: string; referral_code?: string }) {
    return api.post<AuthResponse & { isNewUser: boolean }>('/auth/google', data);
  },

  apple(data: { id_token: string; name?: string; timezone?: string; referral_code?: string }) {
    return api.post<AuthResponse & { isNewUser: boolean }>('/auth/apple', data);
  },

  forgotPassword(email: string) {
    return api.post<{ message: string }>('/auth/forgot-password', { email });
  },

  resetPassword(data: { token: string; new_password: string }) {
    return api.post<{ message: string }>('/auth/reset-password', data);
  },

  me() {
    return api.get<{ user: AuthResponse['user'] }>('/auth/me');
  },

  updateProfile(data: {
    name?: string;
    email?: string;
    timezone?: string;
    weekly_goal_target?: number;
    notification_settings?: Record<string, unknown>;
    birthday?: string | null;
  }) {
    return api.patch<{ user: AuthResponse['user'] }>('/auth/profile', data);
  },

  updatePushToken(data: { push_token: string; platform: 'ios' | 'android' | 'web' }) {
    return api.patch<{ message: string }>('/auth/push-token', data);
  },

  updateTimezone(timezone: string) {
    return api.patch<{ timezone: string }>('/auth/timezone', { timezone });
  },

  logout() {
    return api.post<{ message: string }>('/auth/logout');
  },

  deleteAccount(password: string) {
    return api.delete<{ message: string; scheduledHardDelete: string }>('/auth/account', {
      password,
    });
  },

  exportData() {
    return api.get<Record<string, unknown>>('/auth/export');
  },

  getReferrals() {
    return api.get<{
      referralCode: string;
      totalReferrals: number;
      referrals: { name: string; createdAt: string }[];
    }>('/auth/referrals');
  },
};

// ─── Contacts ────────────────────────────────────────────────────────────────

export interface ApiContact {
  id: number;
  name: string;
  communicationChannels?: { type: string; value: string }[];
  photoUrl?: string;
  relationshipType: string;
  contactFrequencyDays: number;
  birthday?: string;
  tags?: string[];
  yearMet?: number;
  innerCircle: boolean;
  preferredCommMethod?: string;
  healthScore: number | null;
  status: 'on_track' | 'needs_attention' | 'overdue' | 'new';
  daysSinceContact: number;
  lastInteractionDate?: string;
  lastInteractionType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export const contactsApi = {
  list(params?: {
    status?: string;
    relationship_type?: string;
    inner_circle?: boolean;
    search?: string;
    sort?: string;
    order?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<ApiContact>>(`/contacts${query}`);
  },

  get(id: number) {
    return api.get<ApiContact>(`/contacts/${id}`);
  },

  create(data: {
    name: string;
    communication_channels?: { type: string; value: string }[];
    photo_url?: string;
    relationship_type?: string;
    contact_frequency_days?: number;
    birthday?: string;
    tags?: string[];
    year_met?: number;
    inner_circle?: boolean;
    preferred_comm_method?: string;
  }) {
    return api.post<ApiContact>('/contacts', data);
  },

  update(id: number, data: Record<string, unknown>) {
    return api.put<ApiContact>(`/contacts/${id}`, data);
  },

  delete(id: number) {
    return api.delete<{ undoToken: string; expiresIn: number }>(`/contacts/${id}`);
  },

  restore(id: number, undoToken: string) {
    return api.post<ApiContact>(`/contacts/${id}/restore`, { undo_token: undoToken });
  },

  overdue(params?: { limit?: number; offset?: number }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<ApiContact>>(`/contacts/overdue${query}`);
  },
};

// ─── Contact Notes ───────────────────────────────────────────────────────────

export interface ApiNote {
  id: number;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export const notesApi = {
  list(contactId: number, params?: { limit?: number; offset?: number }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<ApiNote>>(`/contacts/${contactId}/notes${query}`);
  },

  create(contactId: number, text: string) {
    return api.post<ApiNote>(`/contacts/${contactId}/notes`, { text });
  },

  delete(contactId: number, noteId: number) {
    return api.delete<void>(`/contacts/${contactId}/notes/${noteId}`);
  },
};

// ─── Interactions ────────────────────────────────────────────────────────────

export interface ApiInteraction {
  id: number;
  contactId: number;
  contactName?: string;
  contactPhotoUrl?: string;
  interactionType: string;
  interactionDate: string;
  notes?: string;
  rating?: string;
  durationMinutes?: number;
  location?: string;
  attachments?: string[];
  createdAt: string;
}

export const interactionsApi = {
  list(params?: {
    contact_id?: number;
    type?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<ApiInteraction>>(`/interactions${query}`);
  },

  create(data: {
    contact_id: number;
    interaction_type: string;
    interaction_date?: string;
    notes?: string;
    rating?: string;
    duration_minutes?: number;
    location?: string;
    attachments?: File[];
  }) {
    if (data.attachments && data.attachments.length > 0) {
      const formData = new FormData();
      formData.append('contact_id', String(data.contact_id));
      formData.append('interaction_type', data.interaction_type);
      if (data.interaction_date) formData.append('interaction_date', data.interaction_date);
      if (data.notes) formData.append('notes', data.notes);
      if (data.rating) formData.append('rating', data.rating);
      if (data.duration_minutes) formData.append('duration_minutes', String(data.duration_minutes));
      if (data.location) formData.append('location', data.location);
      data.attachments.forEach((file) => formData.append('attachments', file));
      return api.upload<ApiInteraction>('/interactions', formData);
    }
    const { attachments: _, ...rest } = data;
    return api.post<ApiInteraction>('/interactions', rest);
  },

  update(id: number, data: Record<string, unknown>) {
    return api.put<ApiInteraction>(`/interactions/${id}`, data);
  },

  delete(id: number) {
    return api.delete<{ undoToken: string; expiresIn: number }>(`/interactions/${id}`);
  },

  restore(id: number, undoToken: string) {
    return api.post<ApiInteraction>(`/interactions/${id}/restore`, { undo_token: undoToken });
  },
};

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface ApiDashboard {
  overallHealthScore: number;
  healthSummary: {
    onTrackCount: number;
    needsAttentionCount: number;
    overdueCount: number;
  };
  overdueContacts: {
    id: number;
    name: string;
    photoUrl?: string;
    daysSinceContact: number;
    healthScore: number;
    lastInteractionType?: string;
  }[];
  upcomingBirthdays: {
    id: number;
    name: string;
    photoUrl?: string;
    birthday: string;
    daysUntil: number;
    turningAge?: number | null;
  }[];
  recentInteractions: {
    id: number;
    contactName: string;
    contactPhotoUrl?: string;
    interactionType: string;
    interactionDate: string;
  }[];
  weeklyStats: {
    interactionsThisWeek: number;
    contactsImproved: number;
    healthScoreChange: number | null;
    currentStreak: number;
    longestStreak: number;
  };
}

export const dashboardApi = {
  get() {
    return api.get<ApiDashboard>('/dashboard');
  },
};

// ─── Activity ────────────────────────────────────────────────────────────────

export interface ApiActivity {
  data: {
    id: number;
    contactId: number;
    contactName: string;
    contactPhotoUrl?: string;
    interactionType: string;
    interactionDate: string;
    notes?: string;
    rating?: string;
    attachments?: string[];
  }[];
  weeklyTrends: { name: string; value: number }[];
  monthlyTrends: { name: string; value: number }[];
  weeklyTotal: number;
  monthlyTotal: number;
  weeklyChangePct: number;
  monthlyChangePct: number;
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

export const activityApi = {
  get(params?: { limit?: number; offset?: number; type?: string; since?: string }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<ApiActivity>(`/activity${query}`);
  },
};

// ─── Reminders ───────────────────────────────────────────────────────────────

export interface ApiReminder {
  id: number;
  contactId: number;
  contactName?: string;
  contactPhotoUrl?: string;
  reminderType: string;
  title: string;
  message?: string;
  priority: number;
  isDismissed: boolean;
  snoozedUntil?: string;
  createdAt: string;
}

export const remindersApi = {
  list(params?: { limit?: number; offset?: number }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<ApiReminder>>(`/reminders${query}`);
  },

  snooze(id: number, durationDays: number) {
    return api.patch<ApiReminder>(`/reminders/${id}/snooze`, { duration_days: durationDays });
  },

  snoozeBirthday(contactId: number, durationDays: number) {
    return api.post<{ message: string }>('/reminders/snooze-birthday', {
      contact_id: contactId,
      duration_days: durationDays,
    });
  },

  dismiss(id: number) {
    return api.patch<ApiReminder>(`/reminders/${id}/dismiss`);
  },
};

// ─── Notifications ───────────────────────────────────────────────────────────

export interface NotificationHistoryItem {
  id: number;
  notificationType: string;
  title: string;
  body: string;
  contactId: number | null;
  contactName: string | null;
  wasOpened: boolean;
  sentAt: string;
  metadata: { ticketId?: number } | null;
}

export const notificationsApi = {
  upcoming() {
    return api.get<{ data: Record<string, unknown>[] }>('/notifications/upcoming');
  },

  history(params?: { limit?: number; offset?: number; unread_only?: boolean }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<PaginatedResponse<NotificationHistoryItem>>(`/notifications/history${query}`);
  },

  unreadCount() {
    return api.get<{ count: number }>('/notifications/unread-count');
  },

  markAsRead(ids: number[]) {
    return api.patch<{ updated: number }>('/notifications/read', { ids });
  },
};

// ─── Upload ──────────────────────────────────────────────────────────────────

export const uploadApi = {
  photo(file: File, target?: { type: 'user' | 'contact' | 'none'; id?: number }) {
    const formData = new FormData();
    formData.append('photo', file);
    if (target) {
      formData.append('target_type', target.type);
      if (target.id !== undefined) formData.append('target_id', String(target.id));
    }
    return api.upload<{ url: string }>('/upload/photo', formData);
  },
};

// ─── Support / Feedback ─────────────────────────────────────────────────────

export interface ApiSupportTicket {
  id: number;
  topic: string;
  message: string;
  attachments: string[];
  status: string;
  adminNotes: string | null;
  hasUnreadAdminReply: boolean;
  resolvedAt: string | null;
  createdAt: string;
  lastMessageText: string | null;
}

export interface ApiTicketMessage {
  id: number;
  ticketId: number;
  senderType: 'user' | 'admin';
  senderId: number;
  message: string;
  attachments: string[];
  createdAt: string;
  senderName?: string;
}

export const supportApi = {
  create(data: { topic: string; message: string }, attachments?: File[]) {
    const formData = new FormData();
    formData.append('topic', data.topic);
    formData.append('message', data.message);
    if (attachments) {
      attachments.forEach((file) => formData.append('attachments', file));
    }
    return api.upload<ApiSupportTicket>('/support', formData);
  },

  list(params?: { limit?: number; offset?: number; status?: string }) {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return api.get<
      PaginatedResponse<ApiSupportTicket> & { closedCount?: number; unreadCount?: number }
    >(`/support${query}`);
  },

  unreadCount() {
    return api.get<{ count: number }>('/support/unread-count');
  },

  getTicket(ticketId: number) {
    return api.get<{ ticket: ApiSupportTicket; messages: ApiTicketMessage[] }>(
      `/support/${ticketId}`,
    );
  },

  getMessages(ticketId: number) {
    return api.get<{ data: ApiTicketMessage[] }>(`/support/${ticketId}/messages`);
  },

  addMessage(ticketId: number, message: string, attachments?: File[]) {
    const formData = new FormData();
    formData.append('message', message);
    if (attachments) {
      attachments.forEach((file) => formData.append('attachments', file));
    }
    return api.upload<ApiTicketMessage>(`/support/${ticketId}/messages`, formData);
  },
};

// ─── Sync ────────────────────────────────────────────────────────────────────

export const syncApi = {
  /** Legacy single-call sync (backward compatible) */
  migrate(data: {
    mode: 'migrate';
    contacts: Record<string, unknown>[];
    interactions: Record<string, unknown>[];
    notes: Record<string, unknown>[];
    weekly_goal_target?: number;
  }) {
    return api.post<{
      syncedContacts: { localId?: string; serverId: number; name: string }[];
      syncedInteractions: number;
      syncedNotes: number;
      conflicts: { field: string; localValue: unknown; serverValue: unknown }[];
    }>('/sync', data);
  },

  /** Step 1: Create contacts and upload photos */
  migrateContacts(data: { contacts: Record<string, unknown>[] }) {
    return api.post<{
      syncedContacts: { localId: string; serverId: number; name: string }[];
      contactMap: Record<string, number>;
    }>('/sync/contacts', data);
  },

  /** Step 2: Create interactions with attachments using contact map */
  migrateInteractions(data: {
    contact_map: Record<string, number>;
    interactions: Record<string, unknown>[];
  }) {
    return api.post<{
      syncedInteractions: number;
      skippedInteractions: number;
    }>('/sync/interactions', data);
  },

  /** Step 3: Create notes, set weekly goal, recalculate health scores */
  migrateFinalize(data: {
    contact_map: Record<string, number>;
    notes: Record<string, unknown>[];
    weekly_goal_target?: number;
  }) {
    return api.post<{
      syncedNotes: number;
      skippedNotes: number;
      healthScoresRecalculated: number;
    }>('/sync/finalize', data);
  },
};
